import 'aws-amplify/auth/enable-oauth-listener'

import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import awsConfig from 'src/aws-exports.json'
import {
  getUserPoolClientIdCookieName,
  getUserPoolIdCookieName,
} from 'src/context/UserProvider/UserProvider'
import { setJwtCookie } from 'src/cookies'
import { useTranslate } from 'src/localization/Trns'
import { homeUrl } from 'src/navigation/urls'
import { createPopulateUserInfoLegacyAction } from 'src/redux/actions/auth.actions'
import { selectEnvConfigFromState } from 'src/redux/selectors/session.selectors'
import Cookies from 'universal-cookie'

export default function SsoCallbackPage() {
  const dispatch = useDispatch()
  const envConfig = useSelector(selectEnvConfigFromState)
  const [isProcessing, setIsProcessing] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const history = useHistory()
  const translate = useTranslate()

  useEffect(() => {
    const exchangeCodeForTokens = async () => {
      try {
        // Extract authorization code from URL
        const searchParams = new URLSearchParams(window.location.search)
        const code = searchParams.get('code')

        if (!code) {
          throw new Error('No authorization code found in URL')
        }

        // Get Cognito config from cookies
        const cookies = new Cookies()
        const userPoolClientId = cookies.get(
          getUserPoolClientIdCookieName(envConfig.ENVIRONMENT),
        )
        const userPoolId = cookies.get(
          getUserPoolIdCookieName(envConfig.ENVIRONMENT),
        )

        if (!userPoolClientId || !userPoolId) {
          throw new Error(
            'Missing Cognito configuration. Please sign in again.',
          )
        }

        // Retrieve PKCE code_verifier from localStorage
        // Amplify stores it with key: CognitoIdentityServiceProvider.{clientId}.oauthPKCE
        const pkceKey = `CognitoIdentityServiceProvider.${userPoolClientId}.oauthPKCE`
        const codeVerifier = window.localStorage.getItem(pkceKey)

        if (!codeVerifier) {
          throw new Error('Missing PKCE code verifier. Please sign in again.')
        }

        // Construct Cognito domain
        const cognitoDomain =
          envConfig.ENVIRONMENT === 'prod'
            ? awsConfig['prod-oauth'].domain
            : awsConfig['stage-oauth'].domain
        const tokenEndpoint = `https://${cognitoDomain}oauth2/token`

        // Get current redirect URI
        const redirectUri = `${window.location.origin}${window.location.pathname}`

        // Exchange authorization code for tokens (including PKCE code_verifier)
        const tokenResponse = await axios.post(
          tokenEndpoint,
          new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: userPoolClientId,
            code: code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
          }).toString(),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          },
        )

        const { access_token } = tokenResponse.data

        if (!access_token) {
          throw new Error('No access token received from OAuth provider')
        }

        // Store JWT in cookie
        setJwtCookie(envConfig, access_token)

        const decodedToken = JSON.parse(window.atob(access_token.split('.')[1]))

        // Extract userId from the 'sub' claim in the JWT
        const userId = decodedToken.sub

        // Dispatch Redux action to update auth state with all required fields
        dispatch(
          createPopulateUserInfoLegacyAction({
            jwt: access_token,
            userId: userId,
          }),
        )

        history.push(homeUrl())
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error during SSO callback:', err)
        setError(
          err instanceof Error
            ? err.message
            : translate('There was an error. Please try again'),
        )
        setIsProcessing(false)
      }
    }

    exchangeCodeForTokens()
  }, [dispatch, envConfig, history])
  // TODO: Make the page look prettier and add a button here to redirect the user back to
  // sign in if the handshake fails for whatever reason
  if (error) {
    return (
      <div>
        <div>{translate('Authentication Error')}</div>
        <div>{error}</div>
      </div>
    )
  }
  // TODO: Let's make a prettier callback page so it's not just text.
  // Also let's translate these strings too
  return <div>{isProcessing ? 'Logging you in securely...' : 'Complete'}</div>
}
